import React, { FunctionComponent, useCallback, useContext, useEffect, useMemo } from 'react';
import { Callout, DirectionalHint, getTheme, mergeStyleSets, Text } from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import useHover from 'react-use-hover';
import { flatCategoryTraits, IFlatTraitDef } from '../../services';
import { ReviewContext } from './ReviewContextProvider';

const styles = mergeStyleSets({
  button: {
    width: 130
  },
  callout: {
    width: 338,
    height: 504,
    padding: '5px 5px'
  },
  title: {
    marginBottom: 12
  },
  link: {
    display: 'block',
    marginTop: 20
  }
});

const listTraits = (list: string[]) => (
  <div style={{ paddingBottom: '15px' }}>
    {list.map(example => (
      <Text block variant="medium" key={example}>
        - {example}
      </Text>
    ))}
  </div>
);

interface TraitDefinitionsProps {
  name: string;
  hoverTrait: string;
  setHoverTrait: React.Dispatch<React.SetStateAction<string>>;
}

const TraitDefinitions: FunctionComponent<TraitDefinitionsProps> = props => {
  const theme = getTheme();
  const traitId = useId('callout-trait-id');
  const [isHovering, hoverProps] = useHover({ mouseEnterDelayMS: 500, mouseLeaveDelayMS: 100 });
  const themeColor = theme.palette.themeSecondary;
  const Label = useCallback(
    props => (
      <div
        style={{
          display: 'flex',
          fontWeight: 600,
          fontSize: '10px',
          alignItems: 'center',
          justifyContent: 'flex-start',
          color: themeColor
        }}
      >
        <div style={{ border: `1px solid ${themeColor}`, width: '5px' }} />
        <div style={{ display: 'flex' }}>{props.children}</div>
        <div style={{ border: `1px solid ${themeColor}`, flexGrow: 100 }} />
      </div>
    ),
    []
  );
  const traitCreator = useCallback((title: string, definition?: string, list?: string[]) => {
    const subSection = list ? (
      listTraits(list)
    ) : (
      <Text block variant="medium">
        {definition}
      </Text>
    );

    return (
      <>
        <Label>{title}</Label>
        <div style={{ padding: '5px' }}>{subSection}</div>
      </>
    );
  }, []);

  useEffect(() => {
    if (isHovering) {
      props.setHoverTrait(props.name);
    }
  }, [isHovering]);

  const itemStyle: React.CSSProperties = {
    color: theme.palette.black,
    fontSize: theme.fonts.medium.fontSize,
    width: '100%',
    textAlign: 'start'
  };
  const { state } = useContext(ReviewContext);

  const queueData = state?.queue?.categorizedTraitDefinitions;
  const allTraits = queueData && flatCategoryTraits(queueData);
  const traitInfo: IFlatTraitDef | undefined = useMemo(
    () => allTraits?.find(trait => trait.trait.toLowerCase() === props.name.toLowerCase()),
    [props.name]
  );
  return (
    <>
      <span className="pl-3 py-2" style={itemStyle} id={traitId} {...hoverProps}>
        {props.name}
      </span>
      {allTraits && isHovering && traitInfo && props.hoverTrait === props.name && (
        <Callout
          role="dialog"
          gapSpace={0}
          target={`#${traitId}`}
          className={styles.callout}
          setInitialFocus
          directionalHint={DirectionalHint.leftTopEdge}
          beakWidth={25}
          {...hoverProps}
        >
          <Text block variant="xLarge" style={{ padding: '5px', color: theme.palette.black }}>
            {traitInfo.trait}
          </Text>
          {traitCreator('Trait Definition', traitInfo.definition)}
          {traitCreator('Trait Examples', '', traitInfo.examples)}
          {traitInfo.exemptions && traitCreator('Trait Exemptions', '', traitInfo.exemptions)}
        </Callout>
      )}
    </>
  );
};

export default TraitDefinitions;
